import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Save, Building2, CreditCard, FileSignature, Palette, Image as ImageIcon, ScrollText, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function Field({ label, name, type = "text", value, onChange, placeholder, fullWidth }) {
  return (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(name, e.target.value)}
        data-testid={`settings-${name}`}
        placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/10 transition text-sm"
      />
    </div>
  );
}

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-6">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-emerald-700" />
        </div>
        <div>
          <div className="font-semibold text-stone-900">{title}</div>
          <div className="text-xs text-stone-500 mt-0.5">{subtitle}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

export default function Settings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => setData((await api.get("/settings/company")).data);
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-stone-500">Loading...</div>;

  const update = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: updated } = await api.put("/settings/company", data);
      setData(updated);
      toast.success("Company settings saved — applied to all future PDF exports");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Only Super Admin can edit settings" : "Save failed");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-emerald-700 font-semibold mb-2">Branding</p>
          <h1 className="text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "Bricolage Grotesque" }}>Company Settings</h1>
          <p className="text-stone-500 mt-2">These details appear on every Proposal PDF — header, signature block and footer.</p>
        </div>
      </div>

      <Section icon={Building2} title="Company Identity" subtitle="Shown in the PDF header and footer">
        <Field label="Company Name" name="company_name" value={data.company_name} onChange={update} fullWidth />
        <Field label="Tagline" name="tagline" value={data.tagline} onChange={update} fullWidth placeholder="e.g. EC · ECMP · MPCB · CRZ — your environmental partner" />
        <Field label="Phone" name="phone" value={data.phone} onChange={update} />
        <Field label="Email" name="email" value={data.email} onChange={update} type="email" />
        <Field label="Website" name="website" value={data.website} onChange={update} />
        <Field label="City / State" name="city_state" value={data.city_state} onChange={update} />
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Registered Address</label>
          <textarea
            value={data.address || ""}
            onChange={(e) => update("address", e.target.value)}
            data-testid="settings-address"
            rows={2}
            className="mt-1 w-full px-3 py-2 bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-emerald-700 text-sm"
          />
        </div>
        <Field label="GST Number" name="gst" value={data.gst} onChange={update} />
        <Field label="CIN" name="cin" value={data.cin} onChange={update} />
        <Field label="PAN" name="pan" value={data.pan} onChange={update} />
      </Section>

      <Section icon={ImageIcon} title="Logo & Colors" subtitle="Logo URL must be a public image (PNG / JPG). Recommend 200×200, square.">
        <Field label="Logo URL" name="logo_url" value={data.logo_url} onChange={update} fullWidth placeholder="https://your-domain.com/logo.png" />
        <Field label="Primary Color (hex)" name="primary_color" value={data.primary_color} onChange={update} placeholder="#0D433F" />
        <Field label="Accent Color (hex)" name="accent_color" value={data.accent_color} onChange={update} placeholder="#D97706" />
        {data.logo_url && (
          <div className="md:col-span-2 flex items-center gap-3 p-3 bg-stone-50 border border-stone-200 rounded-lg">
            <span className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Preview</span>
            <img src={data.logo_url} alt="logo" className="h-10 w-auto border border-stone-200 rounded" onError={(e) => { e.target.style.display = "none"; }} />
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs text-stone-500">Colors</span>
              <span className="w-6 h-6 rounded border border-stone-200" style={{ background: data.primary_color }} />
              <span className="w-6 h-6 rounded border border-stone-200" style={{ background: data.accent_color }} />
            </div>
          </div>
        )}
      </Section>

      <Section icon={FileSignature} title="Authorised Signatory" subtitle="Appears in the signature block on the final page">
        <Field label="Signatory Name" name="signatory_name" value={data.signatory_name} onChange={update} />
        <Field label="Designation" name="signatory_designation" value={data.signatory_designation} onChange={update} />
      </Section>

      <Section icon={CreditCard} title="Bank Account" subtitle="Printed on the PDF for client remittance">
        <Field label="Bank Name" name="bank_name" value={data.bank_name} onChange={update} />
        <Field label="Branch" name="bank_branch" value={data.bank_branch} onChange={update} />
        <Field label="Account Number" name="bank_account" value={data.bank_account} onChange={update} />
        <Field label="IFSC Code" name="bank_ifsc" value={data.bank_ifsc} onChange={update} />
      </Section>

      <Section icon={ScrollText} title="Terms & Conditions" subtitle="A separate page in every PDF. Use a blank line between clauses.">
        <div className="md:col-span-2">
          <textarea
            value={data.terms_and_conditions || ""}
            onChange={(e) => update("terms_and_conditions", e.target.value)}
            data-testid="settings-terms_and_conditions"
            rows={18}
            className="mt-1 w-full px-3 py-3 bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-emerald-700 text-sm font-mono leading-relaxed"
          />
          <div className="text-[11px] text-stone-500 mt-2">{(data.terms_and_conditions || "").length} characters · approximately {Math.ceil((data.terms_and_conditions || "").length / 1800)} A4 page(s)</div>
        </div>
      </Section>

      <div className="flex items-center justify-between sticky bottom-4 bg-white border border-stone-200 rounded-xl px-6 py-4 shadow-lg">
        <div className="text-xs text-stone-600">
          Tip: Generate any Proposal PDF after saving to see your branded layout.
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="settings-save"
          className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition disabled:opacity-60"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
